/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { InternalAxiosRequestConfig, AxiosResponse } from "axios";

import { LoginAction } from "../pages/login/ducks/slices";
import GroupPromise from "./GroupPromise";
import { ErrorAction, ErrorMessage } from "../reducers/slices/errorSlice";
import { authenticatedUserApi } from "./api";
import { CustomAxiosError } from "../types/common";
import { SignUpPath } from "../pages/signup";
import {
  ERROR_CODE,
  handleNetworkFailure,
  isLoginRateLimited,
  notifyRequestSucceeded,
  toLoginRateLimitError,
  trimObject,
} from "@advapacs/common";

const TIMEOUT = 1 * 60 * 1000;
axios.defaults.timeout = TIMEOUT;
axios.defaults.withCredentials = true;
axios.defaults.baseURL = import.meta.env.VITE_APP_API_URL;

// Pages where an UNAUTHENTICATED error doesn't log the user out and reload.
const NO_LOGOUT_PATHS: string[] = [
  "/",
  "/Login",
  "/SSO/SAML2",
  SignUpPath.INDIVIDUAL,
  SignUpPath.ORGANISATION,
  SignUpPath.SELECT,
  "/ForgotPassword",
  "/ResetPassword",
  "/RegisterComplete",
  "/public/terms-of-service",
];

// Pages where a 401 doesn't trigger a token refresh.
const NO_REFRESH_PATHS: string[] = [
  SignUpPath.SELECT,
  SignUpPath.INDIVIDUAL,
  SignUpPath.ORGANISATION,
  "/Login",
  "/ResetPassword",
  "/ForgotPassword",
  "/RegisterComplete",
  "/Auth/EmailValidation",
  "/SSO/SAML2",
  "/Studies/Share",
  "/public/terms-of-service",
];

const RECAPTCHA_EXCEPTIONS = [
  "RecaptchaRiskException",
  "RecaptchaInvalidException",
];

// 401 codes that aren't an expired session, and the one that always is.
const NON_EXPIRY_CODES = ["1003", "1004", "1005", "1013"];
const TOKEN_EXPIRED_CODE = "1007";

interface ApiErrorData {
  type?: string;
  code?: string;
  errors?: ErrorMessage[];
}

const trimStringParams = (params: Record<string, any>) => {
  Object.keys(params).forEach((key) => {
    if (params[key] && typeof params[key] === "string") {
      params[key] = params[key].trim();
    }
  });
};

const getRefreshTokenUrl = () => {
  const region = localStorage.getItem("region") || "";
  return region
    ? `${authenticatedUserApi.REFRESH_TOKEN}?region=${region}`
    : authenticatedUserApi.REFRESH_TOKEN;
};

// Rejected to the UI as a translatable message so it can retry up to three
// times before showing the error.
const toRecaptchaError = (err: CustomAxiosError) => ({
  ...err,
  response: {
    ...err.response,
    data: {
      ...(err.response?.data as ApiErrorData),
      translate: true,
      message: "recaptcha_exception",
      params: {
        traceId: err?.response?.headers["x-trace-id"],
      },
    },
  },
});

const setupAxiosInterceptors = (store: any, brand: any): void => {
  const clearAuthentication = () =>
    store.dispatch(LoginAction.clearAuthentication({ isReloadBrowser: false }));

  const onRequestSuccess = (config: InternalAxiosRequestConfig) => {
    const loginState = store.getState().loginReducer;
    const token = loginState?.csrfToken;
    if (token) {
      axios.defaults.headers.common["X-XSRF-TOKEN"] = token;
      axios.defaults.headers.common["Accept-Language"] =
        loginState?.account?.localisation?.locale || window.navigator.language;
      axios.defaults.headers.common["X-TZ"] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    if (config.data) {
      config.data = trimObject(config.data);
    }
    if (config.params) {
      trimStringParams(config.params);
    }
    return config;
  };

  const onResponseSuccess = (response: AxiosResponse) => {
    notifyRequestSucceeded();
    return response;
  };

  const onResponseError = async (err: CustomAxiosError) => {
    const status = err?.response?.status;
    if (status === undefined) {
      // Shown by NetworkStatusAlert ("Unable to connect to API" / offline).
      return handleNetworkFailure(err);
    }

    const apiError = err?.response?.data as ApiErrorData | undefined;
    if (status === 400 && RECAPTCHA_EXCEPTIONS.includes(apiError?.type ?? "")) {
      return Promise.reject(toRecaptchaError(err));
    }
    if (isLoginRateLimited(err)) {
      return Promise.reject(toLoginRateLimitError(err));
    }

    store.dispatch(ErrorAction.setError(apiError?.errors));
    const sessionHasBeenFetched =
      store.getState().loginReducer?.sessionHasBeenFetched;
    const { pathname } = window.location;

    // Session expired: log out and reload, except on public/auth pages.
    const brandPath = `/brand/${brand?.brandPath || ""}`;
    const shouldReload =
      !NO_LOGOUT_PATHS.includes(pathname) && !pathname.startsWith(brandPath);
    if (
      apiError?.code === ERROR_CODE.UNAUTHENTICATED &&
      sessionHasBeenFetched &&
      shouldReload
    ) {
      clearAuthentication();
      localStorage.setItem("path", pathname); // expired token.
      localStorage.removeItem("region"); // expired token to make next login user will have the default region.
      window.location.reload();
    }

    if (apiError?.code === ERROR_CODE.IP_FORBIDDEN) {
      store.dispatch(LoginAction.restrictIp());
    }

    const isSessionExpired =
      status === 401 &&
      !NO_REFRESH_PATHS.includes(pathname) &&
      ((sessionHasBeenFetched &&
        !NON_EXPIRY_CODES.includes(apiError?.code ?? "")) ||
        apiError?.code === TOKEN_EXPIRED_CODE);

    const originalRequest = err.config;
    if (isSessionExpired && !originalRequest?._retry) {
      originalRequest._retry = true;
      const refreshPromise = GroupPromise.execute(getRefreshTokenUrl());
      if (refreshPromise) {
        try {
          await refreshPromise;
        } catch {
          localStorage.removeItem("region"); // clear if refresh token get 401.
          clearAuthentication();
        }
        return axios.request(originalRequest);
      }
    }

    return Promise.reject(err);
  };

  // @ts-ignore
  axios.interceptors.request.use(onRequestSuccess);
  axios.interceptors.response.use(onResponseSuccess, onResponseError);
};

export default setupAxiosInterceptors;
